import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, ChevronDown, ChevronRight, GripVertical, Save, X, ArrowUp, ArrowDown, Upload, Image as ImageIcon, Layers, Share2 } from 'lucide-react';
import { categoryAPI } from '../../../config/category-api';
import { authAPI } from '../../../config/auth-api';
import { IS_ALFAJR } from '../../../constants/general';
import { filterCategoriesByAccess } from '../../../utils/categoryAccessFilter';
import './Categories.css';
import { toast } from 'react-toastify';
import BannerSelectionModal from '../../Common/BannerSelectionModal/BannerSelectionModal';
import DeleteConfirmationModal from '../../Common/DeleteConfirmationModal/DeleteConfirmationModal';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';

const Categories = ({ adminData }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_CATEGORIES);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_CATEGORIES);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_CATEGORIES);
  const canEditBanner = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_BANNER_PRODUCTS);

  const transformSubCategoryData = (subCategories) => {
    return (subCategories || []).map((sub, subIndex) => ({
      id: sub._id,
      title: sub.title.charAt(0).toUpperCase() + sub.title.slice(1),
      arabicTitle: sub.ar || '',
      image: sub.image || null,
      isActive: true,
      isHidden: sub.isHidden || false,
      isGeneral: sub.isGeneral || false,
      order: sub.order || subIndex + 1,
      seoData: sub.seoData || {},
    }));
  };

  const transformCategoryData = (data) => {
    return data.map((category, index) => ({
      id: category._id,
      title: category.title.charAt(0).toUpperCase() + category.title.slice(1),
      arabicTitle: category.ar,
      image: category.image,
      icon: category.icon,
      color: category.color,
      description: category.description,
      isActive: true,
      isHidden: category.isHidden || false,
      order: category.order || index + 1,
      seoData: category.seoData || {},
      subcategories: transformSubCategoryData(category.subCategories)
    }));
  };

  const getCategoryShareSlug = (category) => {
    const rawTitle = (category.title || '').toLowerCase().trim();
    return (category.seoData?.urlSlug?.trim() || rawTitle);
  };

  const [expandedCategories, setExpandedCategories] = useState({});
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [parentCategoryId, setParentCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    arabicName: '',
    image: null,
    imagePreview: null,
    icon: null,
    iconPreview: null,
    color: '',
    description: '',
    isHidden: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showGeneralSubDialog, setShowGeneralSubDialog] = useState(false);
  const [showGeneralSubListDialog, setShowGeneralSubListDialog] = useState(false);
  const [generalSubcategories, setGeneralSubcategories] = useState([]);
  const [generalSubForm, setGeneralSubForm] = useState({
    name: '',
    arabicName: '',
    isHidden: false
  });
  const [generalSubLoading, setGeneralSubLoading] = useState(false);
  const [selectedCategoryForGeneral, setSelectedCategoryForGeneral] = useState(null);
  const [showBannerSelection, setShowBannerSelection] = useState(false);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await categoryAPI.getCategories();

      if (response.success) {
        setCategories(filterCategoriesByAccess(transformCategoryData(response.data)));
      } else {
        setError('Failed to fetch categories');
      }
    } catch (err) {
      setError('Error fetching categories: ' + err.message);
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralSubcategories = async () => {
    try {
      const response = await categoryAPI.getGeneralSubcategories();
      if (response.success) {
        setGeneralSubcategories(response.data);
      }
    } catch (err) {
      console.error('Error fetching general subcategories:', err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchGeneralSubcategories();
  }, []);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleReorderMode = () => {
    if (isReorderMode) {
      // Exit reorder mode - revert to original order if there are unsaved changes
      if (hasUnsavedChanges) {
        setCategories([...originalCategories]);
      }
      setIsReorderMode(false);
      setHasUnsavedChanges(false);
      setExpandedCategories({});
    } else {
      // Enter reorder mode - save current state as original
      setOriginalCategories([...categories]);
      setIsReorderMode(true);
      // Expand all categories when entering reorder mode
      const allExpanded = categories.reduce((acc, cat) => {
        acc[cat.id] = true;
        return acc;
      }, {});
      setExpandedCategories(allExpanded);
    }
  };

  const moveCategory = (categoryId, direction) => {
    const currentIndex = categories.findIndex(cat => cat.id === categoryId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= categories.length) return;

    const newCategories = [...categories];
    const [movedCategory] = newCategories.splice(currentIndex, 1);
    newCategories.splice(newIndex, 0, movedCategory);

    // Update order values
    const updatedCategories = newCategories.map((cat, index) => ({
      ...cat,
      order: index + 1
    }));

    setCategories(updatedCategories);
    setHasUnsavedChanges(true);
  };

  const moveSubcategory = (subcategoryId, parentCategoryId, direction) => {
    const category = categories.find(cat => cat.id === parentCategoryId);
    if (!category) return;

    const currentIndex = category.subcategories.findIndex(sub => sub.id === subcategoryId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= category.subcategories.length) return;

    const newSubcategories = [...category.subcategories];
    const [movedSubcategory] = newSubcategories.splice(currentIndex, 1);
    newSubcategories.splice(newIndex, 0, movedSubcategory);

    // Update order values
    const updatedSubcategories = newSubcategories.map((sub, index) => ({
      ...sub,
      order: index + 1
    }));

    const updatedCategories = categories.map(cat =>
      cat.id === parentCategoryId
        ? { ...cat, subcategories: updatedSubcategories }
        : cat
    );

    setCategories(updatedCategories);
    setHasUnsavedChanges(true);
  };

  const handleAddGeneralSubcategory = () => {
    setGeneralSubForm({
      name: '',
      arabicName: '',
      isHidden: false
    });
    setShowGeneralSubDialog(true);
  };

  const handleSaveGeneralSubcategory = async () => {
    if (!generalSubForm.name.trim() || !generalSubForm.arabicName.trim()) {
      toast.error('Please fill in both name and Arabic name');
      return;
    }

    try {
      setGeneralSubLoading(true);

      const payload = {
        title: generalSubForm.name.toLowerCase(),
        ar: generalSubForm.arabicName,
        isHidden: generalSubForm.isHidden,
        order: generalSubcategories.length + 1
      };

      const response = await categoryAPI.addGeneralSubcategory(payload);

      if (response.success) {
        toast.success("General subcategory added successfully!");
        setShowGeneralSubDialog(false);
        setGeneralSubForm({ name: '', arabicName: '', isHidden: false });
        await fetchGeneralSubcategories();
      } else {
        toast.error(response.msg || 'Failed to add general subcategory');
      }
    } catch (error) {
      console.error('Error saving general subcategory:', error);
      toast.error('Error saving general subcategory.');
    } finally {
      setGeneralSubLoading(false);
    }
  };

  const handleShowGeneralSubList = (categoryId) => {
    setSelectedCategoryForGeneral(categoryId);
    setShowGeneralSubListDialog(true);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      const reorderData = {
        categories: categories
          .filter(cat => {
            const original = originalCategories.find(o => o.id === cat.id);
            if (!original) return true;
            if (original.order !== cat.order) return true;
            return cat.subcategories.some(sub => {
              const oldSub = original.subcategories.find(s => s.id === sub.id);
              return oldSub && oldSub.order !== sub.order;
            });
          })
          .map(cat => {
            const original = originalCategories.find(o => o.id === cat.id);
            return {
              id: cat.id,
              order: cat.order,
              oldOrder: original?.order,
              subcategories: cat.subcategories
                .map(sub => {
                  const oldSub = original?.subcategories.find(s => s.id === sub.id);
                  return {
                    id: sub.id,
                    order: sub.order,
                    oldOrder: oldSub?.order
                  };
                })
                .filter(sub => sub.oldOrder !== sub.order)
            };
          })
      };

      if (reorderData.categories.length === 0) {
        toast.info("No changes to save");
        setIsReorderMode(false);
        return;
      }

      const response = await categoryAPI.reorderCategories(reorderData);

      if (response.success) {
        const transformedCategories = transformCategoryData(response.data);
        setCategories(transformedCategories);
        setOriginalCategories(transformedCategories);
        setHasUnsavedChanges(false);
        setIsReorderMode(false);
        setExpandedCategories({});
        toast.success('Category order saved successfully!');
      } else {
        throw new Error(response.msg || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (categoryId, isSubcategory = false) => {
    if (isSubcategory) {
      // Find the subcategory in all categories
      let subcategory = null;
      let parentCategory = null;

      for (const cat of categories) {
        const sub = cat.subcategories.find(sub => sub.id === categoryId);
        if (sub) {
          subcategory = sub;
          parentCategory = cat;
          break;
        }
      }

      if (subcategory && parentCategory) {
        setEditingSubcategory(subcategory);
        setParentCategoryId(parentCategory.id);
        setEditingCategory(null);
        setCategoryForm({
          name: subcategory.title,
          arabicName: subcategory.arabicTitle,
          image: null,
          imagePreview: subcategory.image,
          icon: null,
          iconPreview: null,
          color: '',
          description: '',
          isHidden: subcategory.isHidden || false,
          isGeneral: subcategory.isGeneral || false,
          clearImage: false,
          clearIcon: false
        });
        setShowCategoryDialog(true);
      }
      return;
    }

    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      setEditingCategory(category);
      setEditingSubcategory(null);
      setParentCategoryId(null);
      setCategoryForm({
        name: category.title,
        arabicName: category.arabicTitle,
        image: null,
        imagePreview: category.image,
        icon: null,
        iconPreview: category.icon || null,
        color: category.color || '',
        description: category.description || '',
        isHidden: category.isHidden || false,
        clearImage: false,
        clearIcon: false
      });
      setShowCategoryDialog(true);
    }
  };

  const resetForm = () => {
    setCategoryForm({
      name: '',
      arabicName: '',
      image: null,
      imagePreview: null,
      icon: null,
      iconPreview: null,
      color: '',
      description: '',
      isHidden: false,
      clearImage: false,
      clearIcon: false
    });
    setEditingCategory(null);
    setEditingSubcategory(null);
    setParentCategoryId(null);
  };

  const handleAddSubcategory = (categoryId) => {
    resetForm();
    setParentCategoryId(categoryId);
    setShowCategoryDialog(true);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setEditingSubcategory(null);
    setParentCategoryId(null);
    setCategoryForm({
      name: '',
      arabicName: '',
      image: null,
      imagePreview: null,
      icon: null,
      iconPreview: null,
      color: '',
      description: '',
      isHidden: false
    });
    setShowCategoryDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (200KB limit)
      if (file.size > 200 * 1024) {
        toast.error('Image size must be less than 200KB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setCategoryForm(prev => ({
          ...prev,
          image: file,
          imagePreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is PNG
      if (file.type !== 'image/png') {
        toast.error('Icon must be a PNG file');
        return;
      }
      // Check file size (200KB limit)
      if (file.size > 200 * 1024) {
        toast.error('Icon size must be less than 200KB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setCategoryForm(prev => ({
          ...prev,
          icon: file,
          iconPreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim() || !categoryForm.arabicName.trim()) {
      toast.error('Please fill in both name and Arabic name');
      return;
    }

    try {
      setSaveLoading(true);

      if (editingCategory) {
        const payload = {
          title: categoryForm.name.toLowerCase(),
          _id: editingCategory.id,
          ar: categoryForm.arabicName,
          color: categoryForm.color,
          description: categoryForm.description,
          isHidden: categoryForm.isHidden,
          clearIcon: categoryForm.clearIcon,
          clearImage: categoryForm.clearImage
        };

        const response = await categoryAPI.updateCategory(payload, categoryForm.icon, categoryForm.image);

        if (response.success) {
          setCategories(prev => prev.map(cat =>
            cat.id === editingCategory.id
              ? {
                ...cat,
                title: categoryForm.name.charAt(0).toUpperCase() + categoryForm.name.slice(1),
                arabicTitle: categoryForm.arabicName,
                icon: response.data.icon !== undefined
                  ? response.data.icon
                  : cat.icon,
                image: response.data.image !== undefined
                  ? response.data.image
                  : cat.image,
                color: categoryForm.color,
                description: categoryForm.description,
                isHidden: categoryForm.isHidden
              }
              : cat
          ));
          toast.success("Category updated successfully!");
          setShowCategoryDialog(false);
          resetForm();
        } else {
          toast.error(response.msg || 'Failed to update category');
        }
      }
      else if (editingSubcategory && parentCategoryId) {
        const parentCategory = categories.find(cat => cat.id === parentCategoryId);
        const parentCategoryIdValue = parentCategory.title.toLowerCase().replace(/\s/g, '');

        const payload = {
          title: categoryForm.name.toLowerCase(),
          _id: parentCategoryId,
          subCategoryId: editingSubcategory.id,
          ar: categoryForm.arabicName,
          isHidden: categoryForm.isHidden,
          categoryId: parentCategoryIdValue,
          order: editingSubcategory.order,
          clearImage: categoryForm.clearImage
        };

        const response = await categoryAPI.updateCategory(payload, null, categoryForm.image);

        if (response.success) {
          const updatedSubcategories = transformSubCategoryData(response.data.subCategories);

          setCategories(prev =>
            prev.map(cat =>
              cat.id === parentCategoryId
                ? { ...cat, subcategories: updatedSubcategories }
                : cat
            )
          );

          toast.success("Subcategory updated successfully!");
          setShowCategoryDialog(false);
          resetForm();
        } else {
          toast.error(response.msg || 'Failed to update subcategory');
        }
      }
      else if (parentCategoryId) {
        const parentCategory = categories.find(cat => cat.id === parentCategoryId);
        const parentCategoryIdValue = parentCategory.title.toLowerCase().replace(/\s/g, '');

        const lastSubcategoryOrder = parentCategory && parentCategory.subcategories.length > 0
          ? Math.max(...parentCategory.subcategories.map(sub => sub.order || 0))
          : 0;

        const payload = {
          title: categoryForm.name.toLowerCase(),
          _id: parentCategoryId,
          ar: categoryForm.arabicName,
          isHidden: categoryForm.isHidden,
          order: lastSubcategoryOrder + 1,
          categoryId: parentCategoryIdValue,
        };

        const response = await categoryAPI.addCategory(payload, null, categoryForm.image);

        if (response.success) {
          const updatedSubcategories = transformSubCategoryData(response.data.subCategories);

          setCategories(prev => prev.map(cat =>
            cat.id === parentCategoryId
              ? { ...cat, subcategories: updatedSubcategories }
              : cat
          ));

          toast.success("Subcategory added successfully!");
          setShowCategoryDialog(false);
          resetForm();
        } else {
          toast.error(response.msg || 'Failed to add subcategory');
        }
      }
      else {
        const lastCategoryOrder = categories.length > 0
          ? Math.max(...categories.map(cat => cat.order || 0))
          : 0;

        const payload = {
          title: categoryForm.name.toLowerCase(),
          ar: categoryForm.arabicName,
          color: categoryForm.color,
          description: categoryForm.description,
          isHidden: categoryForm.isHidden,
          order: lastCategoryOrder + 1
        };

        const response = await categoryAPI.addCategory(payload, categoryForm.icon, categoryForm.image);

        if (response.success) {
          const newCategory = {
            id: response.data._id,
            title: categoryForm.name.charAt(0).toUpperCase() + categoryForm.name.slice(1),
            arabicTitle: response.data.ar,
            image: response.data.image,
            icon: response.data.icon,
            color: categoryForm.color,
            description: categoryForm.description,
            isActive: true,
            isHidden: categoryForm.isHidden,
            order: response.data.order || categories.length + 1,
            subcategories: []
          };

          setCategories(prev => [...prev, newCategory]);
          toast.success("Category added successfully!");
          setShowCategoryDialog(false);
          resetForm();
        } else {
          toast.error(response.msg || 'Failed to add category');
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error saving category. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowCategoryDialog(false);
    resetForm();
  };

  const handleShareCategory = (category) => {
    const domain = IS_ALFAJR ? 'fajr.qa' : 'ablemartonline.com';
    const pathPrefix = IS_ALFAJR ? '' : '/qatar';
    const categorySlug = getCategoryShareSlug(category);
    const url = `https://${domain}${pathPrefix}/order/${encodeURIComponent(categorySlug)}/c/${category.id || category._id}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Category link copied!"))
      .catch(err => {
        console.error("Failed to copy link:", err);
        toast.error("Failed to copy link");
      });
  };

  const handleShareSubcategory = (category, subcategory) => {
    const domain = IS_ALFAJR ? 'fajr.qa' : 'ablemartonline.com';
    const pathPrefix = IS_ALFAJR ? '' : '/qatar';
    const categorySlug = getCategoryShareSlug(category);
    const url = `https://${domain}${pathPrefix}/order/${encodeURIComponent(categorySlug)}/c/${category.id || category._id}?subCategory=${encodeURIComponent(subcategory.title.toLowerCase())}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Subcategory link copied!"))
      .catch(err => {
        console.error("Failed to copy link:", err);
        toast.error("Failed to copy link");
      });
  };



  const handleTranslateTitle = async (text, formType) => {
    if (!text || text.trim() === '') return;

    try {
      const response = await authAPI.translateText(text);
      if (response.success && response.data) {
        if (formType === 'category') {
          setCategoryForm(prev => ({ ...prev, arabicName: response.data }));
        } else if (formType === 'general') {
          setGeneralSubForm(prev => ({ ...prev, arabicName: response.data }));
        }
      }
    } catch (error) {
      console.error('Translation failed:', error);
    }
  };

  const handleSelectGeneralSub = async (generalSub) => {
    if (!selectedCategoryForGeneral) return;

    try {
      setSaveLoading(true);

      const parentCategory = categories.find(cat => cat.id === selectedCategoryForGeneral);
      const parentCategoryIdValue = parentCategory.title.toLowerCase().replace(/\s/g, '');

      const lastSubcategoryOrder = parentCategory && parentCategory.subcategories.length > 0
        ? Math.max(...parentCategory.subcategories.map(sub => sub.order || 0))
        : 0;

      const payload = {
        title: generalSub.title.toLowerCase(),
        _id: selectedCategoryForGeneral,
        ar: generalSub.ar,
        isHidden: generalSub.isHidden,
        order: lastSubcategoryOrder + 1,
        categoryId: parentCategoryIdValue,
        isGeneral: true,
        generalSubCategoryId: generalSub._id
      };

      const response = await categoryAPI.addCategory(payload);

      if (response.success) {
        const updatedSubcategories = transformSubCategoryData(response.data.subCategories);

        setCategories(prev => prev.map(cat =>
          cat.id === selectedCategoryForGeneral
            ? { ...cat, subcategories: updatedSubcategories }
            : cat
        ));

        toast.success("General subcategory added successfully!");
        setShowGeneralSubListDialog(false);
        setSelectedCategoryForGeneral(null);
      } else {
        toast.error(response.msg || 'Failed to add general subcategory');
      }
    } catch (error) {
      console.error('Error adding general subcategory:', error);
      toast.error('Error adding general subcategory.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = (categoryId, isSubcategory = false) => {
    let itemName = "";
    if (isSubcategory) {
      for (const cat of categories) {
        const sub = cat.subcategories.find(sub => sub.id === categoryId);
        if (sub) {
          itemName = sub.title;
          break;
        }
      }
    } else {
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        itemName = category.title;
        if (category.subcategories && category.subcategories.length > 0) {
          toast.error('Category has subcategories. Delete them first.'); return;
        }
      }
    }

    setDeleteItem({ id: categoryId, isSubcategory, name: itemName });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setDeleteLoading(true);

      if (deleteItem.isSubcategory) {
        // Find the parent category
        let parentCategory = null;
        let subcategoryToDelete = null;

        for (const cat of categories) {
          const sub = cat.subcategories.find(sub => sub.id === deleteItem.id);
          if (sub) {
            subcategoryToDelete = sub;
            parentCategory = cat;
            break;
          }
        }

        if (parentCategory) {
          const response = await categoryAPI.deleteSubcategory(parentCategory.id, subcategoryToDelete.id);

          if (response.success) {
            // Remove subcategory from state
            setCategories(prev => prev.map(cat =>
              cat.id === parentCategory.id
                ? { ...cat, subcategories: cat.subcategories.filter(sub => sub.id !== deleteItem.id) }
                : cat
            ));
            toast.success("Subcategory deleted successfully!");
          } else {
            toast.error(response.msg || 'Failed to delete subcategory');
          }
        }
      } else {
        // Delete main category
        const response = await categoryAPI.deleteCategory(deleteItem.id);

        if (response.success) {
          // Remove category from state
          setCategories(prev => prev.filter(cat => cat.id !== deleteItem.id));
          toast.success("Category deleted successfully!");
        } else {
          toast.error(response.msg || 'Failed to delete category');
        }
      }

      setShowDeleteDialog(false);
      setDeleteItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Error deleting item. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteItem(null);
  };

  return (
    <div className="category-categories-page">
      <div className="category-page-header">
        <div className="category-page-title">
          <h1>Categories</h1>
          <p>Organize with categories and subcategories</p>
        </div>
        <div className="category-header-actions">
          {isReorderMode ? (
            <div className="category-reorder-actions">
              {canEdit && (
                <button
                  className="category-save-changes-btn"
                  onClick={handleSaveChanges}
                  disabled={!hasUnsavedChanges || isSaving}
                >
                  <Save size={20} />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
              <button
                className="category-exit-reorder-btn"
                onClick={toggleReorderMode}
              >
                <X size={20} />
                Exit Reorder
              </button>
            </div>
          ) : (
            <div className="category-normal-actions">
              {canEdit && (
                <button
                  className="category-reorder-mode-btn"
                  onClick={toggleReorderMode}
                >
                  <GripVertical size={20} />
                  Reorder
                </button>
              )}
              {canCreate && (
                <>
                  <button
                    className="category-add-category-btn category-add-general-sub-btn"
                    onClick={handleAddGeneralSubcategory}
                  >
                    <Plus size={20} />
                    Add Gen SubCat
                  </button>
                  <button className="category-add-category-btn" onClick={handleAddCategory}>
                    <Plus size={20} />
                    Add Category
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {isReorderMode && (
        <div className="category-reorder-instructions">
          <p>💡 Use the arrow buttons to reorder categories and subcategories. Click "Save Changes" when done.</p>
        </div>
      )}

      <div className="category-categories-content">
        {loading ? (
          <div className="category-loading-state">
            <div className="category-loading-spinner"></div>
            <p>Loading categories...</p>
          </div>
        ) : error ? (
          <div className="category-error-state">
            <p>{error}</p>
            <button onClick={fetchCategories} className="category-retry-button">
              Retry
            </button>
          </div>
        ) : categories.length === 0 ? (
          <div className="category-empty-state">
            <p>No categories found</p>
          </div>
        ) : (
          <div className="category-categories-grid">
            {categories
              .sort((a, b) => a.order - b.order)
              .map((category, index) => (
                <div key={category.id} className={`category-category-card ${isReorderMode ? 'category-reorder-mode' : ''} ${category.isHidden ? 'category-hidden-category' : ''}`}>
                  <div className="category-category-header">
                    {isReorderMode && (
                      <div className="category-reorder-controls">
                        <button
                          className="category-move-btn"
                          onClick={() => moveCategory(category.id, 'up')}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          className="category-move-btn"
                          onClick={() => moveCategory(category.id, 'down')}
                          disabled={index === categories.length - 1}
                          title="Move down"
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                    )}
                    <div className="category-category-image">
                      {category.image ? (
                        <img
                          src={category.image}
                          alt={category.title}
                          className="category-category-image-img"
                        />
                      ) : (
                        <span className="category-no-image">No Image</span>
                      )}
                    </div>
                    <div className="category-category-info">
                      <div className="category-category-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h3 className="category-category-title" style={{ margin: 0 }}>{category.title}</h3>
                        <button
                          className="category-title-share-btn"
                          type="button"
                          onClick={() => handleShareCategory(category)}
                          title="Copy link"
                        >
                          <Share2 size={14} />
                        </button>
                        {category.isHidden && (
                          <span className="category-hidden-badge">Hidden</span>
                        )}
                      </div>
                      <p className="category-category-arabic" style={{ margin: 0 }}>{category.arabicTitle}</p>
                    </div>
                    {!isReorderMode && (
                      <div className="category-category-actions">
                        {canCreate && (
                          <>
                            <button
                              className="category-action-btn category-add-general-sub-btn"
                              onClick={() => handleShowGeneralSubList(category.id)}
                              title="Show General Subcategory List"
                            >
                              <Layers size={16} />
                            </button>
                            <button
                              className="category-action-btn category-add-subcategory-btn"
                              onClick={() => handleAddSubcategory(category.id)}
                              title="Add Subcategory"
                            >
                              <Plus size={16} />
                            </button>
                          </>
                        )}
                        {canEdit && (
                          <button
                            className="category-action-btn category-edit-btn"
                            onClick={() => handleEdit(category.id)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="category-action-btn category-delete-btn"
                            onClick={() => handleDelete(category.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {category.subcategories && category.subcategories.length > 0 && (
                    <div className="category-subcategories-section">
                      {!isReorderMode && (
                        <button
                          className="category-subcategories-toggle"
                          onClick={() => toggleCategory(category.id)}
                        >
                          <span className="category-subcategories-label">
                            Subcategories ({category.subcategories.length})
                          </span>
                          {expandedCategories[category.id] ?
                            <ChevronDown size={16} /> :
                            <ChevronRight size={16} />
                          }
                        </button>
                      )}

                      {(isReorderMode || expandedCategories[category.id]) && (
                        <div className="category-subcategories-list">
                          {category.subcategories
                            .sort((a, b) => a.order - b.order)
                            .map((subcategory, subIndex) => (
                              <div key={subcategory.id} className={`category-subcategory-item ${isReorderMode ? 'category-reorder-mode' : ''} ${subcategory.isHidden ? 'category-hidden-subcategory' : ''}`}>
                                {isReorderMode && (
                                  <div className="category-reorder-controls">
                                    <button
                                      className="category-move-btn"
                                      onClick={() => moveSubcategory(subcategory.id, category.id, 'up')}
                                      disabled={subIndex === 0}
                                      title="Move up"
                                    >
                                      <ArrowUp size={14} />
                                    </button>
                                    <button
                                      className="category-move-btn"
                                      onClick={() => moveSubcategory(subcategory.id, category.id, 'down')}
                                      disabled={subIndex === category.subcategories.length - 1}
                                      title="Move down"
                                    >
                                      <ArrowDown size={14} />
                                    </button>
                                  </div>
                                )}
                                <div className="category-subcategory-image">
                                  {subcategory.image ? (
                                    <img
                                      src={subcategory.image}
                                      alt={subcategory.title}
                                      className="category-subcategory-image-img"
                                    />
                                  ) : (
                                    <span className="category-no-image">No Image</span>
                                  )}
                                </div>
                                <div className="category-subcategory-info">
                                  <div className="category-subcategory-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <h4 className="category-subcategory-title" style={{ margin: 0 }}>{subcategory.title}</h4>
                                    <button
                                      className="category-title-share-btn"
                                      type="button"
                                      onClick={() => handleShareSubcategory(category, subcategory)}
                                      title="Copy link"
                                    >
                                      <Share2 size={12} />
                                    </button>
                                    {subcategory.isGeneral && (
                                      <span className="category-general-badge">GENERAL</span>
                                    )}
                                    {subcategory.isHidden && (
                                      <span className="category-hidden-badge category-small">Hidden</span>
                                    )}
                                  </div>
                                  <p className="category-subcategory-arabic" style={{ margin: 0 }}>{subcategory.arabicTitle}</p>
                                </div>
                                {!isReorderMode && (canEdit || canDelete) && (
                                  <div className="category-subcategory-actions">
                                    {canEdit && !subcategory.isGeneral && (<button
                                      className="category-action-btn category-edit-btn"
                                      onClick={() => handleEdit(subcategory.id, true)}
                                      title="Edit"
                                    >
                                      <Edit size={14} />
                                    </button>)}
                                    {canDelete && (
                                      <button
                                        className="category-action-btn category-delete-btn"
                                        onClick={() => handleDelete(subcategory.id, true)}
                                        title="Delete"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Category Dialog */}
      {showCategoryDialog && (
        <div className="category-dialog-overlay">
          <div className="category-dialog">
            <div className="category-dialog-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h2>
                  {editingCategory ? 'Edit Category' :
                    editingSubcategory ? 'Edit Subcategory' :
                      parentCategoryId ? 'Add Subcategory' : 'Add New Category'}
                </h2>
                 {editingCategory && (
                  <button
                    type="button"
                    onClick={() => handleShareCategory(editingCategory)}
                    className="category-modal-share-btn"
                    title="Copy link"
                  >
                    <Share2 size={14} />
                    Share Category
                  </button>
                )}
                {editingSubcategory && (
                  <button
                    type="button"
                    onClick={() => {
                      const parent = categories.find(c => c.id === parentCategoryId);
                      if (parent) handleShareSubcategory(parent, editingSubcategory);
                    }}
                    className="category-modal-share-btn"
                    title="Copy link"
                  >
                    <Share2 size={14} />
                    Share Subcategory
                  </button>
                )}
                {editingSubcategory && canEditBanner && (
                  <button
                    type="button"
                    onClick={() => setShowBannerSelection(true)}
                    className="category-banner-assignment-btn"
                  >
                    Banner with this subcategory
                  </button>
                )}
              </div>
              <button className="category-dialog-close" onClick={handleCloseDialog}>
                <X size={20} />
              </button>
            </div>

            <div className="category-dialog-content">
              <div className="category-form-group">
                <label htmlFor="categoryName">
                  {editingSubcategory || parentCategoryId ? 'Subcategory Name' : 'Category Name'}
                </label>
                <input
                  type="text"
                  id="categoryName"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  onBlur={(e) => handleTranslateTitle(e.target.value, 'category')}
                  placeholder={editingSubcategory || parentCategoryId ? "Enter subcategory name" : "Enter category name"}
                  className="category-form-input"
                />
              </div>

              <div className="category-form-group">
                <label htmlFor="categoryArabicName">Arabic Name</label>
                <input
                  type="text"
                  id="categoryArabicName"
                  value={categoryForm.arabicName}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, arabicName: e.target.value }))}
                  placeholder="أدخل اسم الفئة"
                  className="category-form-input"
                  dir="rtl"
                />
              </div>

              {!editingSubcategory && !parentCategoryId && !IS_ALFAJR && (
                <>
                  <div className="category-form-group">
                    <label htmlFor="categoryDescription">Description</label>
                    <textarea
                      id="categoryDescription"
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter category description"
                      className="category-form-input"
                      rows="3"
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  <div className="category-form-group">
                    <label htmlFor="categoryColor">Color</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="color"
                        id="categoryColor"
                        value={categoryForm.color || '#000000'}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                        style={{ width: '60px', height: '40px', cursor: 'pointer', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                      />
                      <input
                        type="text"
                        value={categoryForm.color || ''}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                        placeholder="#000000"
                        className="category-form-input"
                        style={{ flex: 1 }}
                      />
                      {categoryForm.color && (
                        <button
                          type="button"
                          onClick={() => setCategoryForm(prev => ({ ...prev, color: '' }))}
                          title="Clear color"
                          className="category-clear-color-btn"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="category-form-group">
                    <label htmlFor="categoryIcon">Banner Image (PNG only)</label>
                    <div className="category-image-upload-section">
                      <div className="category-image-preview">
                        {categoryForm.iconPreview ? (
                          <div className="category-image-preview-container" style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                              src={categoryForm.iconPreview}
                              alt="Icon Preview"
                              className='category-image-preview-emoji'
                              style={{ maxWidth: '64px', maxHeight: '64px' }}
                            />
                            <button
                              type="button"
                              onClick={() => setCategoryForm(prev => ({
                                ...prev,
                                icon: null,
                                iconPreview: null,
                                clearIcon: true
                              }))}
                              title="Remove icon"
                              className="category-clear-icon-btn"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="category-image-placeholder">
                            <ImageIcon size={32} />
                            <span>No icon selected</span>
                          </div>
                        )}
                      </div>

                      <div className="category-image-upload-controls">
                        <input
                          type="file"
                          id="categoryIcon"
                          accept="image/png"
                          onChange={handleIconChange}
                          className="category-file-input"
                        />
                        <label htmlFor="categoryIcon" className="category-upload-btn">
                          <Upload size={16} />
                          {categoryForm.iconPreview ? 'Change Icon' : 'Choose Icon (PNG)'}
                        </label>
                      </div>
                      <div className="category-file-size-limit">
                        Recommended size: 150px × 150px <br />
                        Maximum file size: 200KB
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="category-form-group">
                <label className="category-toggle-label-text">Visibility Status</label>
                <div className="category-visibility-toggle-container">
                  <label className="category-visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      checked={!categoryForm.isHidden}
                      onChange={() => setCategoryForm(prev => ({ ...prev, isHidden: false }))}
                      className="category-visibility-radio"
                    />
                    <span className="category-visibility-option-content category-visible">
                      <Eye size={18} />
                      <span>Visible</span>
                    </span>
                  </label>
                  <label className="category-visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      checked={categoryForm.isHidden}
                      onChange={() => setCategoryForm(prev => ({ ...prev, isHidden: true }))}
                      className="category-visibility-radio"
                    />
                    <span className="category-visibility-option-content category-hidden">
                      <X size={18} />
                      <span>Hidden</span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="category-form-group">
                <label htmlFor="categoryImage">
                  {(parentCategoryId || editingSubcategory) ? 'Subcategory Image' : 'Category Image'}
                </label>
                <div className="category-image-upload-section">
                  <div className="category-image-preview">
                    {categoryForm.imagePreview ? (
                      <div className="category-image-preview-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={categoryForm.imagePreview}
                          alt="Preview"
                          className='category-image-preview-emoji'
                        />
                        <button
                          type="button"
                          onClick={() => setCategoryForm(prev => ({
                            ...prev,
                            image: null,
                            imagePreview: null,
                            clearImage: true
                          }))}
                          title="Remove image"
                          className="category-clear-icon-btn"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="category-image-placeholder">
                        <ImageIcon size={32} />
                        <span>No image selected</span>
                      </div>
                    )}
                  </div>

                  <div className="category-image-upload-controls">
                    <input
                      type="file"
                      id="categoryImage"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="category-file-input"
                    />
                    <label htmlFor="categoryImage" className="category-upload-btn">
                      <Upload size={16} />
                      {categoryForm.imagePreview ? 'Change Image' : 'Choose Image'}
                    </label>
                  </div>
                  <div className="category-file-size-limit">
                    Recommended size: 150px × 150px <br />
                    Maximum file size: 200KB
                  </div>
                </div>
              </div>
            </div>

            <div className="category-dialog-footer">
              <button className="category-cancel-btn" onClick={handleCloseDialog}>
                Cancel
              </button>
              <button className="category-save-btn" onClick={handleSaveCategory} disabled={saveLoading}>
                {saveLoading ? (
                  <>
                    <Save size={16} />
                    Loading...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editingCategory ? 'Update Category' :
                      editingSubcategory ? 'Update Subcategory' :
                        parentCategoryId ? 'Add Subcategory' : 'Add Category'
                    }
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGeneralSubDialog && (
        <div className="category-dialog-overlay">
          <div className="category-dialog">
            <div className="category-dialog-header">
              <h2>Add General Subcategory</h2>
              <button className="category-dialog-close" onClick={() => setShowGeneralSubDialog(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="category-dialog-content">
              <div className="category-form-group">
                <label htmlFor="generalSubName">Subcategory Name</label>
                <input
                  type="text"
                  value={generalSubForm.name}
                  onChange={(e) => setGeneralSubForm({ ...generalSubForm, name: e.target.value })}
                  onBlur={(e) => handleTranslateTitle(e.target.value, 'general')}
                  placeholder="Enter category name"
                  className="category-form-input"
                  autoFocus
                />
              </div>

              <div className="category-form-group">
                <label htmlFor="generalSubArabicName">Arabic Name</label>
                <input
                  type="text"
                  id="generalSubArabicName"
                  value={generalSubForm.arabicName}
                  onChange={(e) => setGeneralSubForm(prev => ({ ...prev, arabicName: e.target.value }))}
                  placeholder="أدخل اسم الفئة الفرعية"
                  className="category-form-input"
                  dir="rtl"
                />
              </div>

              <div className="category-form-group">
                <label className="category-toggle-label-text">Visibility Status</label>
                <div className="category-visibility-toggle-container">
                  <label className="category-visibility-option">
                    <input
                      type="radio"
                      name="generalVisibility"
                      checked={!generalSubForm.isHidden}
                      onChange={() => setGeneralSubForm(prev => ({ ...prev, isHidden: false }))}
                      className="category-visibility-radio"
                    />
                    <span className="category-visibility-option-content category-visible">
                      <Eye size={18} />
                      <span>Visible</span>
                    </span>
                  </label>
                  <label className="category-visibility-option">
                    <input
                      type="radio"
                      name="generalVisibility"
                      checked={generalSubForm.isHidden}
                      onChange={() => setGeneralSubForm(prev => ({ ...prev, isHidden: true }))}
                      className="category-visibility-radio"
                    />
                    <span className="category-visibility-option-content category-hidden">
                      <X size={18} />
                      <span>Hidden</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="category-dialog-footer">
              <button className="category-cancel-btn" onClick={() => setShowGeneralSubDialog(false)}>
                Cancel
              </button>
              <button
                className="category-save-btn"
                onClick={handleSaveGeneralSubcategory}
                disabled={generalSubLoading}
              >
                {generalSubLoading ? (
                  <>
                    <Save size={16} />
                    Loading...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Add General Subcategory
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGeneralSubListDialog && (
        <div className="category-dialog-overlay">
          <div className="category-dialog">
            <div className="category-dialog-header">
              <h2>Select General Subcategory</h2>
              <button className="category-dialog-close" onClick={() => {
                setShowGeneralSubListDialog(false);
                setSelectedCategoryForGeneral(null);
              }}>
                <X size={20} />
              </button>
            </div>

            <div className="category-dialog-content">
              {saveLoading && (
                <div className="category-loading-state">
                  <div className="category-loading-spinner"></div>
                  <p>Adding...</p>
                </div>
              )}
              {!saveLoading && (
                <>
                  {generalSubcategories.length === 0 ? (
                    <div className="category-empty-state">
                      <p>No general subcategories available</p>
                    </div>
                  ) : (
                    <div className="category-general-sub-grid">
                      {generalSubcategories.map((generalSub) => (
                        <div
                          key={generalSub._id}
                          className="category-general-sub-card"
                          onClick={() => handleSelectGeneralSub(generalSub)}
                        >
                          <div className="category-general-sub-image">
                            {generalSub.image ? (
                              <img src={generalSub.image} alt={generalSub.title} />
                            ) : (
                              <span className="category-no-image">No Image</span>
                            )}
                          </div>

                          <div className="category-general-sub-content">
                            <h4 className="category-general-sub-title">
                              {generalSub.title.charAt(0).toUpperCase() + generalSub.title.slice(1)}
                            </h4>
                            <p className="category-general-sub-ar">{generalSub.ar}</p>
                            {generalSub.isHidden && <span className="category-general-hidden">HIDDEN</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="category-dialog-footer">
              <button
                className="category-cancel-btn"
                onClick={() => {
                  setShowGeneralSubListDialog(false);
                  setSelectedCategoryForGeneral(null);
                }}
                disabled={saveLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteDialog}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={deleteItem?.isSubcategory ? 'Delete Subcategory' : 'Delete Category'}
        message={`Are you sure you want to delete "${deleteItem?.name}"? This action cannot be undone.`}
        isLoading={deleteLoading}
      />

      <BannerSelectionModal
        isOpen={showBannerSelection}
        onClose={() => setShowBannerSelection(false)}
        type="subcategory"
        targetData={{
          category: categories.find(c => c.id === parentCategoryId)?.title || '',
          subCategory: editingSubcategory?.title || ''
        }}
      />
    </div>
  );
};

export default Categories;